import { useState } from 'react';
import { faceFor, isFace, isSeed, type Member } from '@engineer-os/chat';
import { useStrings } from '../../state/useLocale';
import { Operator } from '../avatar/Operator';

/**
 * The marks beside a name, wherever one is shown: the figure, and a small
 * check that says the room, not the browser, vouches for the name.
 *
 * What a member wears is one of three things, and the difference is only
 * visible here. Most people wear an operator — nine integers naming nine
 * parts, drawn 16×16 from the brand ramp in this tab (lib/avatar.ts) — which
 * is what everyone has from their first message whether or not they ever open
 * the builder. Some wear one of the protocol's emoji faces, which is what the
 * product offered before the figures existed and is still a choice. And a
 * signed-in person who has built no figure wears the picture on their account,
 * which is the one of the three that is an image: Google's, cross-origin,
 * served with the header that allows it on this cross-origin-isolated page,
 * and left out entirely if it does not load.
 *
 * A message from before any of this carries nothing, and falls back to the
 * face its tag gives, so a room's left edge is a column of people rather than
 * a column with holes in it. That is presentation and nothing more: it shows
 * what that person has by default, not what they were wearing at the time.
 *
 * State without hue, as everywhere else in the chat: the check is a glyph in
 * the text colour with a title, not a badge. The figures and the faces are the
 * one thing in the panel allowed to be coloured, because they are pictures of
 * people rather than interface.
 */
export function Avatar({ src, tag, size = 16 }: { src: string | null | undefined; tag?: string; size?: number }) {
  const [broken, setBroken] = useState(false);
  if (src && isSeed(src)) return <Operator seed={src} size={size} className="chat-op" />;
  const face = src && isFace(src) ? src : !src && tag ? faceFor(tag) : null;

  if (face) {
    return (
      <span className="chat-face emoji" aria-hidden="true" style={size === 16 ? undefined : { fontSize: `${size}px` }}>
        {face}
      </span>
    );
  }
  if (!src || broken) return null;
  return <img className="chat-avatar" src={src} alt="" width={size} height={size} referrerPolicy="no-referrer" onError={() => setBroken(true)} />;
}

export function VerifiedMark() {
  const s = useStrings().chat;
  return (
    <span className="msg-verified" title={s.signIn.verified} aria-label={s.signIn.verifiedShort}>
      <svg viewBox="0 0 12 12" aria-hidden="true" width="10" height="10">
        <path d="M2.5 6.2l2.2 2.2L9.6 3.6" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

/**
 * The name as every list shows it: figure, name, and the check when it is the
 * account's.
 *
 * With `onOpen` the three of them become one button that opens the card saying
 * who that is (ProfileCard.tsx). It is a real button in the document rather
 * than a click handler on a span, for the reason the message menu is one: this
 * is a workstation, everything in it has a key, and a name that can only be
 * reached with a pointer is a name half the room cannot reach.
 *
 * The button is `inline-flex` with the gap and baseline its parents already
 * use, so wrapping three flex children in one changes what is clickable and
 * nothing about where anything sits. Without `onOpen` the fragment is returned
 * exactly as it always was — a report quote is not somewhere a name should
 * become a way out of the dialog.
 */
export function Identity({
  m,
  onOpen,
}: {
  m: Pick<Member, 'name' | 'verified' | 'avatar' | 'tag'>;
  onOpen?: () => void;
}) {
  const s = useStrings().chat;
  /* A name is the person's own and may be in either script — `cleanName`
     accepts letters from any of them — so it takes its direction from itself
     rather than from the panel. `auto` also isolates it, which is what keeps
     the `#tag` beside it from being dragged into the name's run. */
  const marks = (
    <>
      <Avatar src={m.avatar} tag={m.tag} />
      <span className="msg-name" dir="auto">
        {m.name}
      </span>
      {m.verified && <VerifiedMark />}
    </>
  );

  if (!onOpen) return marks;

  /* No `aria-label`: the name is the button's content, so it is already the
     button's accessible name, and a label here would replace the one thing a
     screen reader most needs to hear with a description of the control. The
     title is the description, which is the arrangement the reply quote beside
     it already uses. */
  return (
    <button type="button" className="msg-who" title={s.who.open} onClick={onOpen}>
      {marks}
    </button>
  );
}
